import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import type { Order } from '@billx/types';
// removed
import { WS_EVENTS, WS_ROOM_TYPES } from '@billx/utils';

import { useCurrentCustomer } from '../customers/useCurrentCustomer';
import { useCustomerWebSocket } from '../socket/useCustomerWebsocket';
import { useCustomerOrder } from './useCustomerOrder';

interface OrderEventPayload {
  data: Order;
  timestamp: string;
}

interface DeleteEventPayload {
  data: string;
  timestamp: string;
}

export const useCustomerOrderSubscription = (
  businessId?: string,
  outletId?: string
) => {
  const { currentCustomer } = useCurrentCustomer(businessId);

  const { activeOrder, queryKey } = useCustomerOrder(
    businessId,
    outletId,
    currentCustomer?.id
  );

  const { subscribe, joinRoom, leaveRoom } = useCustomerWebSocket();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!activeOrder?.id) return;

    joinRoom(WS_ROOM_TYPES.ORDER, activeOrder.id);

    const unsubUpdated = subscribe({
      event: WS_EVENTS.ORDER.UPDATED,
      handler: (payload: OrderEventPayload) => {
        if (payload.data.id === activeOrder.id) {
          queryClient.setQueryData(queryKey, payload.data);
        }
      },
    });

    const unsubDeleted = subscribe({
      event: WS_EVENTS.ORDER.DELETED,
      handler: (payload: DeleteEventPayload) => {
        if (payload.data === activeOrder.id) {
          queryClient.setQueryData(queryKey, null);
        }
      },
    });

    return () => {
      unsubUpdated();
      unsubDeleted();
      leaveRoom(WS_ROOM_TYPES.ORDER, activeOrder.id);
    };
  }, [activeOrder?.id, queryKey, subscribe, joinRoom, leaveRoom, queryClient]);
};
